'use client';
import React, { useState } from 'react';
import { Search, Download, Eye } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

interface Payment {
  id: string;
  receiptNo: string;
  taxType: string;
  period: string;
  amount: number;
  channel: string;
  date: string;
  status: 'paid' | 'pending' | 'overdue';
  reconciled: boolean;
}

// Backend integration point: replace with API call to /api/taxpayer/:tin/payments
const payments: Payment[] = [
  { id: 'pay-001', receiptNo: 'LRA-2026-084421', taxType: 'Withholding Tax', period: 'Apr 2026', amount: 1250.0, channel: 'Orange Money', date: '04/18/2026', status: 'paid', reconciled: true },
  { id: 'pay-002', receiptNo: 'LRA-2026-071203', taxType: 'GST', period: 'Q4 2025', amount: 3840.0, channel: 'Bank Transfer', date: '01/29/2026', status: 'paid', reconciled: true },
  { id: 'pay-003', receiptNo: 'LRA-2026-058912', taxType: 'Corporate Income Tax', period: 'FY 2024', amount: 5200.0, channel: 'Ecobank Liberia', date: '12/15/2025', status: 'paid', reconciled: true },
  { id: 'pay-004', receiptNo: 'LRA-2025-049731', taxType: 'Property Tax', period: 'FY 2025', amount: 780.0, channel: 'MTN Money', date: '09/03/2025', status: 'paid', reconciled: true },
  { id: 'pay-005', receiptNo: 'LRA-2025-038204', taxType: 'Withholding Tax', period: 'Aug 2025', amount: 960.0, channel: 'Orange Money', date: '08/14/2025', status: 'paid', reconciled: true },
  { id: 'pay-006', receiptNo: 'LRA-2025-029847', taxType: 'GST', period: 'Q2 2025', amount: 2710.0, channel: 'Bank Transfer', date: '07/30/2025', status: 'paid', reconciled: false },
  { id: 'pay-007', receiptNo: 'LRA-2025-018334', taxType: 'Excise Duty', period: 'Q1 2025', amount: 440.0, channel: 'POS Terminal', date: '04/22/2025', status: 'paid', reconciled: true },
  { id: 'pay-008', receiptNo: 'LRA-2025-009102', taxType: 'Corporate Income Tax', period: 'FY 2023', amount: 4880.0, channel: 'Ecobank Liberia', date: '01/08/2025', status: 'paid', reconciled: true },
];

const channelColors: Record<string, string> = {
  'Orange Money': '#ff6b00',
  'MTN Money': '#ffcc00',
  'Bank Transfer': '#60a5fa',
  'Ecobank Liberia': '#34d399',
  'POS Terminal': '#a78bfa',
};

interface PaymentHistoryTableProps {
  compact?: boolean;
}

export default function PaymentHistoryTable({ compact = false }: PaymentHistoryTableProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(compact ? 5 : 8);
  const [hovered, setHovered] = useState<string | null>(null);

  const filtered = payments.filter(
    (p) =>
      p.taxType.toLowerCase().includes(search.toLowerCase()) ||
      p.receiptNo.toLowerCase().includes(search.toLowerCase()) ||
      p.period.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      {!compact && (
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
            <input
              type="text"
              placeholder="Search by receipt, tax type..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-8 pr-3 py-2 rounded-lg text-sm outline-none transition-all"
              style={{
                backgroundColor: 'var(--dark-elevated)',
                border: '1px solid var(--dark-border)',
                color: 'var(--dark-text)',
              }}
            />
          </div>
          <button
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:bg-white/10"
            style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
      )}

      {compact && (
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>
          Recent Payments
        </h3>
      )}

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
              {(compact
                ? ['Receipt No.', 'Tax Type', 'Amount', 'Date', 'Status']
                : ['Receipt No.', 'Tax Type', 'Period', 'Amount (USD)', 'Channel', 'Date', 'Reconciled', 'Actions']
              ).map((h) => (
                <th
                  key={`ph-th-${h}`}
                  className="text-left py-2.5 px-2 font-semibold uppercase tracking-wide whitespace-nowrap"
                  style={{ color: 'var(--dark-muted)', fontSize: '10px', letterSpacing: '0.06em' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.map((row) => (
              <tr
                key={row.id}
                onMouseEnter={() => setHovered(row.id)}
                onMouseLeave={() => setHovered(null)}
                className="transition-colors duration-100"
                style={{
                  backgroundColor: hovered === row.id ? 'rgba(255,255,255,0.04)' : 'transparent',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <td className="py-2.5 px-2">
                  <span className="font-mono-data text-xs" style={{ color: 'var(--accent)' }}>
                    {row.receiptNo}
                  </span>
                </td>
                <td className="py-2.5 px-2" style={{ color: 'var(--dark-text)' }}>
                  {row.taxType}
                </td>
                {!compact && (
                  <td className="py-2.5 px-2" style={{ color: 'var(--dark-muted)' }}>
                    {row.period}
                  </td>
                )}
                <td className="py-2.5 px-2 tabular-nums font-semibold" style={{ color: '#4ade80' }}>
                  ${row.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
                {!compact && (
                  <td className="py-2.5 px-2">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{
                        backgroundColor: `${channelColors[row.channel] ?? '#94a3b8'}20`,
                        color: channelColors[row.channel] ?? 'var(--dark-muted)',
                      }}
                    >
                      {row.channel}
                    </span>
                  </td>
                )}
                <td className="py-2.5 px-2 whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>
                  {row.date}
                </td>
                {!compact && (
                  <td className="py-2.5 px-2">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: row.reconciled ? 'rgba(74,222,128,0.1)' : 'rgba(251,146,60,0.1)',
                        color: row.reconciled ? '#4ade80' : '#fb923c',
                      }}
                    >
                      {row.reconciled ? 'Reconciled' : 'Pending'}
                    </span>
                  </td>
                )}
                {compact && (
                  <td className="py-2.5 px-2">
                    <StatusBadge variant="paid" size="sm" />
                  </td>
                )}
                {!compact && (
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ opacity: hovered === row.id ? 1 : 0 }}>
                      <button
                        className="p-1 rounded transition-all hover:bg-white/10"
                        style={{ color: 'var(--dark-muted)' }}
                        title="View receipt"
                        aria-label="View receipt"
                      >
                        <Eye size={13} />
                      </button>
                      <button
                        className="p-1 rounded transition-all hover:bg-white/10"
                        style={{ color: 'var(--dark-muted)' }}
                        title="Download receipt PDF"
                        aria-label="Download receipt PDF"
                      >
                        <Download size={13} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!compact && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: '1px solid var(--dark-border)' }}>
          <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
            Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length} payments
          </p>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-${i + 1}`}
                onClick={() => setPage(i + 1)}
                className="w-7 h-7 rounded text-xs font-medium transition-all"
                style={{
                  backgroundColor: page === i + 1 ? 'var(--accent)' : 'transparent',
                  color: page === i + 1 ? 'var(--accent-foreground)' : 'var(--dark-muted)',
                  border: page === i + 1 ? 'none' : '1px solid var(--dark-border)',
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}